import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { HardHat, Users, CalendarDays, AlertTriangle, Clock, Wrench, FileWarning, TrendingUp, Activity, Briefcase, ChevronDown, ChevronRight } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { useNotification } from '@/components/NotificationProvider';
import { formatDate } from '@/utils/dateUtils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function getAuthHeader() {
  return { Authorization: `Bearer ${localStorage.getItem('token')}` };
}

export default function Dashboard() {
  const notification = useNotification();
  const [stats, setStats] = useState({
    equipment: 0,
    personnel: 0,
    projects: 0,
    alerts: 0
  });
  const [dashboardAlerts, setDashboardAlerts] = useState([]);
  const [equipmentUtilization, setEquipmentUtilization] = useState(null);
  const [personnelUtilization, setPersonnelUtilization] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Collapse state for forecast sections - default collapsed
  const [equipmentForecastOpen, setEquipmentForecastOpen] = useState(false);
  const [personnelForecastOpen, setPersonnelForecastOpen] = useState(false);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const [equipment, personnel, projects, alerts, eqUtilization, persUtilization] = await Promise.all([
        axios.get(`${API}/equipment`, { headers: getAuthHeader() }),
        axios.get(`${API}/personnel`, { headers: getAuthHeader() }),
        axios.get(`${API}/projects`, { headers: getAuthHeader() }),
        axios.get(`${API}/alerts/dashboard`, { headers: getAuthHeader() }),
        axios.get(`${API}/dashboard/equipment_utilization`, { headers: getAuthHeader() }),
        axios.get(`${API}/dashboard/personnel_utilization`, { headers: getAuthHeader() })
      ]);

      setStats({
        equipment: equipment.data.length,
        personnel: personnel.data.length,
        projects: projects.data.filter(p => p.is_active).length,
        alerts: alerts.data.length
      });

      setDashboardAlerts(alerts.data);
      setEquipmentUtilization(eqUtilization.data);
      setPersonnelUtilization(persUtilization.data);
    } catch (error) {
      notification.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    { title: 'Total Equipment', value: stats.equipment, icon: HardHat, color: 'text-slate-900' },
    { title: 'Total Personnel', value: stats.personnel, icon: Users, color: 'text-slate-900' },
    { title: 'Active Projects', value: stats.projects, icon: CalendarDays, color: 'text-slate-900' },
    { title: 'Unread Alerts', value: stats.alerts, icon: AlertTriangle, color: stats.alerts > 0 ? 'text-red-500' : 'text-slate-900' }
  ];

  const getPriorityBadge = (priority) => {
    const variants = {
      high: 'bg-red-50 text-red-700 border-red-200',
      medium: 'bg-yellow-50 text-yellow-700 border-yellow-200',
      low: 'bg-emerald-50 text-emerald-700 border-emerald-200'
    };
    return variants[priority] || variants.low;
  };

  const getAlertIcon = (type) => {
    if (type === 'visa_expiry' || type === 'visa_expired') {
      return <FileWarning className="w-5 h-5 text-orange-500 mt-0.5" />;
    }
    if (type === 'maintenance_due' || type === 'maintenance_overdue') {
      return <Wrench className="w-5 h-5 text-blue-500 mt-0.5" />;
    }
    return <AlertTriangle className="w-5 h-5 text-orange-500 mt-0.5" />;
  };

  // Separate alerts by type
  const visaAlerts = dashboardAlerts.filter(a => a.type === 'visa_expiry' || a.type === 'visa_expired');
  const maintenanceAlerts = dashboardAlerts.filter(a => a.type === 'maintenance_due' || a.type === 'maintenance_overdue');

  // Get utilization color based on percentage
  const getUtilizationColor = (percentage) => {
    if (percentage >= 80) return 'text-emerald-600';
    if (percentage >= 50) return 'text-amber-600';
    return 'text-red-600';
  };

  const getUtilizationBgColor = (percentage) => {
    if (percentage >= 80) return 'bg-emerald-500';
    if (percentage >= 50) return 'bg-amber-500';
    return 'bg-red-500';
  };

  return (
    <div className="p-6 md:p-8 lg:p-12" data-testid="dashboard">
      <div className="mb-8">
        <h1 className="text-4xl font-bold tracking-tight text-slate-900">Dashboard</h1>
        <p className="text-sm text-slate-600 mt-2">Overview of resource planning operations</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        {statCards.map((stat) => {
          const Icon = stat.icon;
          return (
            <Card key={stat.title} className="bg-white border-slate-200 shadow-sm" data-testid={`stat-${stat.title.toLowerCase().replace(/\s+/g, '-')}`}>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-xs font-mono uppercase tracking-wider text-slate-500">
                  {stat.title}
                </CardTitle>
                <Icon className={`w-5 h-5 ${stat.color}`} />
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold font-mono text-slate-900">{stat.value}</div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Equipment Utilisation Widget */}
      <Card className="bg-white border-slate-200 shadow-sm mb-6" data-testid="equipment-utilization">
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <Activity className="w-5 h-5 text-blue-500" />
            <CardTitle className="text-lg font-semibold tracking-tight text-slate-700 uppercase">
              Equipment Utilisation
            </CardTitle>
            <Badge className="bg-blue-100 text-blue-700 border-blue-200 text-xs">Live + 90-Day Forecast</Badge>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p className="text-sm text-slate-500">Loading...</p>
          ) : !equipmentUtilization || equipmentUtilization.total_equipment === 0 ? (
            <div className="text-center py-6">
              <HardHat className="w-8 h-8 mx-auto mb-2 text-slate-300" />
              <p className="text-sm text-slate-500">No equipment data available</p>
              <p className="text-xs text-slate-400 mt-1">Add equipment to see utilisation metrics</p>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Live and Forecast Summary */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* Live Utilization */}
                <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse"></div>
                    <span className="text-xs font-semibold text-slate-600 uppercase">Live Now</span>
                  </div>
                  <div className={`text-4xl font-bold font-mono ${getUtilizationColor(equipmentUtilization.live_utilization.percentage)}`}>
                    {equipmentUtilization.live_utilization.percentage}%
                  </div>
                  <div className="mt-2 text-xs text-slate-500">
                    <span className="font-medium text-slate-700">{equipmentUtilization.live_utilization.allocated}</span> allocated / 
                    <span className="font-medium text-slate-700"> {equipmentUtilization.live_utilization.available}</span> available
                  </div>
                  {/* Progress bar */}
                  <div className="mt-3 h-2 bg-slate-200 rounded-full overflow-hidden">
                    <div 
                      className={`h-full ${getUtilizationBgColor(equipmentUtilization.live_utilization.percentage)} transition-all duration-500`}
                      style={{ width: `${equipmentUtilization.live_utilization.percentage}%` }}
                    ></div>
                  </div>
                </div>

                {/* 90-Day Forecast Average */}
                <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
                  <div className="flex items-center gap-2 mb-3">
                    <TrendingUp className="w-4 h-4 text-blue-500" />
                    <span className="text-xs font-semibold text-slate-600 uppercase">90-Day Avg Forecast</span>
                  </div>
                  <div className={`text-4xl font-bold font-mono ${getUtilizationColor(equipmentUtilization.forecast_utilization.avg_percentage)}`}>
                    {equipmentUtilization.forecast_utilization.avg_percentage}%
                  </div>
                  <div className="mt-2 text-xs text-slate-500">
                    Average projected utilization
                  </div>
                  {/* Progress bar */}
                  <div className="mt-3 h-2 bg-slate-200 rounded-full overflow-hidden">
                    <div 
                      className={`h-full ${getUtilizationBgColor(equipmentUtilization.forecast_utilization.avg_percentage)} transition-all duration-500`}
                      style={{ width: `${equipmentUtilization.forecast_utilization.avg_percentage}%` }}
                    ></div>
                  </div>
                </div>

                {/* Fleet Summary */}
                <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
                  <div className="flex items-center gap-2 mb-3">
                    <HardHat className="w-4 h-4 text-slate-500" />
                    <span className="text-xs font-semibold text-slate-600 uppercase">Fleet Summary</span>
                  </div>
                  <div className="text-4xl font-bold font-mono text-slate-800">
                    {equipmentUtilization.total_equipment}
                  </div>
                  <div className="mt-2 text-xs text-slate-500">
                    Total equipment in fleet
                  </div>
                  <div className="mt-3 flex gap-2">
                    <Badge className="bg-emerald-100 text-emerald-700 text-xs">
                      {equipmentUtilization.live_utilization.allocated} Working
                    </Badge>
                    <Badge className="bg-slate-100 text-slate-600 text-xs">
                      {equipmentUtilization.live_utilization.available} Available
                    </Badge>
                  </div>
                </div>
              </div>

              {/* Weekly Forecast & Category Breakdown - Collapsible */}
              <Collapsible open={equipmentForecastOpen} onOpenChange={setEquipmentForecastOpen}>
                <CollapsibleTrigger className="flex items-center gap-2 w-full text-left hover:bg-slate-50 rounded p-1 -ml-1">
                  {equipmentForecastOpen ? (
                    <ChevronDown className="w-4 h-4 text-slate-400" />
                  ) : (
                    <ChevronRight className="w-4 h-4 text-slate-400" />
                  )}
                  <h4 className="text-xs font-semibold text-slate-600 uppercase">Forecast Details & Category Breakdown</h4>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <div className="space-y-6 mt-3">
                    {/* Weekly Forecast Chart */}
                    {equipmentUtilization.forecast_utilization.weekly_data.length > 0 && (
                      <div>
                        <h5 className="text-xs font-semibold text-slate-500 uppercase mb-3">Weekly Forecast (Next 13 Weeks)</h5>
                        <div className="flex items-end gap-1 h-24">
                          {equipmentUtilization.forecast_utilization.weekly_data.map((week, idx) => (
                            <div 
                              key={idx} 
                              className="flex-1 flex flex-col items-center group"
                              title={`Week ${week.week} (${week.week_start}): ${week.percentage}% (${week.allocated} units)`}
                            >
                              <div 
                                className={`w-full ${getUtilizationBgColor(week.percentage)} rounded-t transition-all hover:opacity-80`}
                                style={{ height: `${Math.max(week.percentage, 5)}%` }}
                              ></div>
                              <span className="text-[9px] text-slate-400 mt-1 group-hover:text-slate-700">
                                {week.week_start}
                              </span>
                            </div>
                          ))}
                        </div>
                        <div className="flex justify-between text-xs text-slate-500 mt-2">
                          <span>This week</span>
                          <span>+90 days</span>
                        </div>
                      </div>
                    )}

                    {/* By Category Breakdown */}
                    {equipmentUtilization.by_category.length > 0 && (
                      <div>
                        <h5 className="text-xs font-semibold text-slate-500 uppercase mb-3">Utilisation by Category</h5>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                          {equipmentUtilization.by_category.map((cat, idx) => (
                            <div 
                              key={idx}
                              className="bg-slate-50 rounded p-3 border border-slate-200"
                              data-testid={`eq-category-${cat.category}`}
                            >
                              <div className="text-xs font-medium text-slate-700 truncate" title={cat.category}>
                                {cat.category}
                              </div>
                              <div className={`text-xl font-bold font-mono ${getUtilizationColor(cat.percentage)}`}>
                                {cat.percentage}%
                              </div>
                              <div className="text-[10px] text-slate-500">
                                {cat.allocated}/{cat.total} allocated
                              </div>
                              <div className="mt-2 h-1 bg-slate-200 rounded-full overflow-hidden">
                                <div 
                                  className={`h-full ${getUtilizationBgColor(cat.percentage)}`}
                                  style={{ width: `${cat.percentage}%` }}
                                ></div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </CollapsibleContent>
              </Collapsible>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Personnel Utilisation Widget */}
      <Card className="bg-white border-slate-200 shadow-sm mb-8" data-testid="personnel-utilization">
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <Briefcase className="w-5 h-5 text-purple-500" />
            <CardTitle className="text-lg font-semibold tracking-tight text-slate-700 uppercase">
              Personnel Utilisation
            </CardTitle>
            <Badge className="bg-purple-100 text-purple-700 border-purple-200 text-xs">Live + 90-Day Forecast</Badge>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p className="text-sm text-slate-500">Loading...</p>
          ) : !personnelUtilization || personnelUtilization.total_personnel === 0 ? (
            <div className="text-center py-6">
              <Users className="w-8 h-8 mx-auto mb-2 text-slate-300" />
              <p className="text-sm text-slate-500">No personnel data available</p>
              <p className="text-xs text-slate-400 mt-1">Add personnel to see utilisation metrics</p>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Live and Forecast Summary */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* Live Utilization */}
                <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-2 h-2 bg-purple-500 rounded-full animate-pulse"></div>
                    <span className="text-xs font-semibold text-slate-600 uppercase">Live Now</span>
                  </div>
                  <div className={`text-4xl font-bold font-mono ${getUtilizationColor(personnelUtilization.live_utilization.percentage)}`}>
                    {personnelUtilization.live_utilization.percentage}%
                  </div>
                  <div className="mt-2 text-xs text-slate-500">
                    <span className="font-medium text-slate-700">{personnelUtilization.live_utilization.allocated}</span> allocated / 
                    <span className="font-medium text-slate-700"> {personnelUtilization.live_utilization.available}</span> available
                  </div>
                  {/* Progress bar */}
                  <div className="mt-3 h-2 bg-slate-200 rounded-full overflow-hidden">
                    <div 
                      className={`h-full bg-purple-500 transition-all duration-500`}
                      style={{ width: `${personnelUtilization.live_utilization.percentage}%` }}
                    ></div>
                  </div>
                </div>

                {/* 90-Day Forecast Average */}
                <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
                  <div className="flex items-center gap-2 mb-3">
                    <TrendingUp className="w-4 h-4 text-purple-500" />
                    <span className="text-xs font-semibold text-slate-600 uppercase">90-Day Avg Forecast</span>
                  </div>
                  <div className={`text-4xl font-bold font-mono ${getUtilizationColor(personnelUtilization.forecast_utilization.avg_percentage)}`}>
                    {personnelUtilization.forecast_utilization.avg_percentage}%
                  </div>
                  <div className="mt-2 text-xs text-slate-500">
                    Average projected utilization
                  </div>
                  {/* Progress bar */}
                  <div className="mt-3 h-2 bg-slate-200 rounded-full overflow-hidden">
                    <div 
                      className={`h-full bg-purple-500 transition-all duration-500`}
                      style={{ width: `${personnelUtilization.forecast_utilization.avg_percentage}%` }}
                    ></div>
                  </div>
                </div>

                {/* Workforce Summary */}
                <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
                  <div className="flex items-center gap-2 mb-3">
                    <Users className="w-4 h-4 text-slate-500" />
                    <span className="text-xs font-semibold text-slate-600 uppercase">Workforce Summary</span>
                  </div>
                  <div className="text-4xl font-bold font-mono text-slate-800">
                    {personnelUtilization.total_personnel}
                  </div>
                  <div className="mt-2 text-xs text-slate-500">
                    Total personnel in workforce
                  </div>
                  <div className="mt-3 flex gap-2">
                    <Badge className="bg-purple-100 text-purple-700 text-xs">
                      {personnelUtilization.live_utilization.allocated} On Project
                    </Badge>
                    <Badge className="bg-slate-100 text-slate-600 text-xs">
                      {personnelUtilization.live_utilization.available} Available
                    </Badge>
                  </div>
                </div>
              </div>

              {/* Weekly Forecast & Role Breakdown - Collapsible */}
              <Collapsible open={personnelForecastOpen} onOpenChange={setPersonnelForecastOpen}>
                <CollapsibleTrigger className="flex items-center gap-2 w-full text-left hover:bg-slate-50 rounded p-1 -ml-1">
                  {personnelForecastOpen ? (
                    <ChevronDown className="w-4 h-4 text-slate-400" />
                  ) : (
                    <ChevronRight className="w-4 h-4 text-slate-400" />
                  )}
                  <h4 className="text-xs font-semibold text-slate-600 uppercase">Forecast Details & Role Breakdown</h4>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <div className="space-y-6 mt-3">
                    {/* Weekly Forecast Chart */}
                    {personnelUtilization.forecast_utilization.weekly_data.length > 0 && (
                      <div>
                        <h5 className="text-xs font-semibold text-slate-500 uppercase mb-3">Weekly Forecast (Next 13 Weeks)</h5>
                        <div className="flex items-end gap-1 h-24">
                          {personnelUtilization.forecast_utilization.weekly_data.map((week, idx) => (
                            <div 
                              key={idx} 
                              className="flex-1 flex flex-col items-center group"
                              title={`Week ${week.week} (${week.week_start}): ${week.percentage}% (${week.allocated} people)`}
                            >
                              <div 
                                className="w-full bg-purple-500 rounded-t transition-all hover:opacity-80"
                                style={{ height: `${Math.max(week.percentage, 5)}%` }}
                              ></div>
                              <span className="text-[9px] text-slate-400 mt-1 group-hover:text-slate-700">
                                {week.week_start}
                              </span>
                            </div>
                          ))}
                        </div>
                        <div className="flex justify-between text-xs text-slate-500 mt-2">
                          <span>This week</span>
                          <span>+90 days</span>
                        </div>
                      </div>
                    )}

                    {/* By Role Breakdown */}
                    {personnelUtilization.by_role.length > 0 && (
                      <div>
                        <h5 className="text-xs font-semibold text-slate-500 uppercase mb-3">Utilisation by Role</h5>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                          {personnelUtilization.by_role.map((role, idx) => (
                            <div 
                              key={idx}
                              className="bg-slate-50 rounded p-3 border border-slate-200"
                              data-testid={`role-${role.role}`}
                            >
                              <div className="text-xs font-medium text-slate-700 truncate" title={role.role}>
                                {role.role}
                              </div>
                              <div className={`text-xl font-bold font-mono ${getUtilizationColor(role.percentage)}`}>
                                {role.percentage}%
                              </div>
                              <div className="text-[10px] text-slate-500">
                                {role.allocated}/{role.total} allocated
                              </div>
                              <div className="mt-2 h-1 bg-slate-200 rounded-full overflow-hidden">
                                <div 
                                  className="h-full bg-purple-500"
                                  style={{ width: `${role.percentage}%` }}
                                ></div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </CollapsibleContent>
              </Collapsible>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Alerts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Visa Expiry Alerts */}
        <Card className="bg-white border-slate-200 shadow-sm" data-testid="visa-alerts">
          <CardHeader className="pb-3">
            <div className="flex items-center gap-2">
              <FileWarning className="w-5 h-5 text-orange-500" />
              <CardTitle className="text-lg font-semibold tracking-tight text-slate-700 uppercase">
                Visa Expiry Alerts
              </CardTitle>
              {visaAlerts.length > 0 && (
                <Badge className="bg-red-100 text-red-700 border-red-200">{visaAlerts.length}</Badge>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <p className="text-sm text-slate-500">Loading...</p>
            ) : visaAlerts.length === 0 ? (
              <div className="text-center py-6">
                <Clock className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                <p className="text-sm text-slate-500">No visa expiry alerts</p>
                <p className="text-xs text-slate-400 mt-1">Personnel visas are up to date</p>
              </div>
            ) : (
              <div className="space-y-3 max-h-[300px] overflow-y-auto">
                {visaAlerts.map((alert) => (
                  <div 
                    key={alert.id} 
                    className={`flex items-start gap-3 p-3 border rounded-sm ${
                      alert.priority === 'high' ? 'border-red-200 bg-red-50/50' : 'border-slate-200'
                    }`}
                    data-testid={`alert-${alert.id}`}
                  >
                    {getAlertIcon(alert.type)}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span className="text-sm font-medium text-slate-900 truncate">{alert.asset_name}</span>
                        <Badge className={getPriorityBadge(alert.priority)}>{alert.priority}</Badge>
                      </div>
                      <p className="text-xs text-slate-600">{alert.message}</p>
                      <p className="text-xs text-slate-500 font-mono mt-1">
                        {formatDate(alert.date)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Maintenance Alerts */}
        <Card className="bg-white border-slate-200 shadow-sm" data-testid="maintenance-alerts">
          <CardHeader className="pb-3">
            <div className="flex items-center gap-2">
              <Wrench className="w-5 h-5 text-blue-500" />
              <CardTitle className="text-lg font-semibold tracking-tight text-slate-700 uppercase">
                Maintenance Alerts
              </CardTitle>
              {maintenanceAlerts.length > 0 && (
                <Badge className="bg-blue-100 text-blue-700 border-blue-200">{maintenanceAlerts.length}</Badge>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <p className="text-sm text-slate-500">Loading...</p>
            ) : maintenanceAlerts.length === 0 ? (
              <div className="text-center py-6">
                <Wrench className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                <p className="text-sm text-slate-500">No maintenance alerts</p>
                <p className="text-xs text-slate-400 mt-1">Set maintenance dates on equipment to see alerts</p>
              </div>
            ) : (
              <div className="space-y-3 max-h-[300px] overflow-y-auto">
                {maintenanceAlerts.map((alert) => (
                  <div 
                    key={alert.id} 
                    className={`flex items-start gap-3 p-3 border rounded-sm ${
                      alert.priority === 'high' ? 'border-red-200 bg-red-50/50' : 'border-slate-200'
                    }`}
                    data-testid={`alert-${alert.id}`}
                  >
                    {getAlertIcon(alert.type)}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span className="text-sm font-medium text-slate-900 truncate">{alert.asset_name}</span>
                        <Badge className={getPriorityBadge(alert.priority)}>{alert.priority}</Badge>
                      </div>
                      <p className="text-xs text-slate-600">{alert.message}</p>
                      <p className="text-xs text-slate-500 font-mono mt-1">
                        {formatDate(alert.date)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
